/**
 * Растровые операции: маскировка данных (blur / пикселизация).
 *
 * ВАЖНО (FR-18/19 ТЗ): эффект применяется ЧЕСТНО к пикселям исходного
 * изображения на canvas, а не через CSS-фильтры. CSS-blur не сохраняется
 * в экспорте и математически обратим при малых радиусах.
 */

export interface MaskRegion {
  x: number;
  y: number;
  w: number;
  h: number;
  mode: 'blur' | 'pixelize';
  intensity: number;
}

/** Применить маскировку к области на canvas-контексте (исходный растр). */
export function applyMask(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  mode: 'blur' | 'pixelize',
  intensity: number,
): void {
  if (w <= 0 || h <= 0) return;
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const iw = Math.ceil(w);
  const ih = Math.ceil(h);

  if (mode === 'blur') {
    const tmp = document.createElement('canvas');
    tmp.width = iw;
    tmp.height = ih;
    const tctx = tmp.getContext('2d')!;
    tctx.drawImage(ctx.canvas, ix, iy, iw, ih, 0, 0, iw, ih);
    tctx.filter = `blur(${intensity}px)`;
    tctx.drawImage(tmp, 0, 0);
    tctx.filter = 'none';
    ctx.drawImage(tmp, ix, iy);
  } else {
    const cell = Math.max(2, intensity);
    const tw = Math.max(1, Math.floor(iw / cell));
    const th = Math.max(1, Math.floor(ih / cell));
    const tmp = document.createElement('canvas');
    tmp.width = tw;
    tmp.height = th;
    const tctx = tmp.getContext('2d')!;
    tctx.imageSmoothingEnabled = false;
    tctx.drawImage(ctx.canvas, ix, iy, iw, ih, 0, 0, tw, th);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(tmp, 0, 0, tw, th, ix, iy, iw, ih);
    ctx.imageSmoothingEnabled = true;
  }
}

/**
 * Отрисовать замаскированную область из исходного изображения на отдельный canvas.
 * Используется для превью масок поверх холста (чтобы пользователь видел результат,
 * а не чёрный прямоугольник).
 */
export function renderMaskedRegion(
  source: CanvasImageSource,
  region: MaskRegion,
): HTMLCanvasElement {
  const { x, y, w, h, mode, intensity } = region;
  const canvas = document.createElement('canvas');
  const iw = Math.max(1, Math.ceil(w));
  const ih = Math.max(1, Math.ceil(h));
  canvas.width = iw;
  canvas.height = ih;
  const ctx = canvas.getContext('2d')!;

  // Сначала копируем участок исходного изображения, затем применяем маску к этому ctx.
  ctx.drawImage(source, Math.floor(x), Math.floor(y), iw, ih, 0, 0, iw, ih);
  applyMask(ctx, 0, 0, iw, ih, mode, intensity);
  return canvas;
}

